/**
 * Browser push notifications for chat messages.
 * Triggers only when the tab is hidden (background/minimized).
 */

import { Capacitor } from '@capacitor/core';

/**
 * Show a browser notification for an incoming chat message.
 * Only fires when `document.hidden === true` to avoid interrupting active users.
 */
export function showMessageNotification(
  senderName: string,
  messagePreview: string,
  roomId: string,
  onTap?: () => void,
): void {
  // Don't notify if the tab is active - the user can see the chat
  if (!document.hidden) return;

  // Native platforms use their own push notification system
  if (Capacitor.isNativePlatform()) return;

  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const notification = new Notification(senderName, {
    body: messagePreview,
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: `chat-${roomId}`, // Coalesce notifications per room
    requireInteraction: false,
    silent: false,
  });

  notification.onclick = () => {
    window.focus();
    onTap?.();
    notification.close();
  };
}

/**
 * Get a human-readable preview string for a message type.
 */
export function getMessageTypePreview(messageType: string | null): string {
  switch (messageType) {
    case 'voice_note': return '🎤 Voice message';
    case 'image': return '📷 Photo';
    case 'video': return '🎬 Video';
    case 'file': return '📎 File';
    case 'event': return '📅 Event';
    case 'poll': return '📊 Poll';
    case 'location': return '📍 Location';
    case 'sticker': return '😊 Sticker';
    default: return '💬 New message';
  }
}
